import { DataTable } from "@/components/shared/data-table";
import { ErrorState } from "@/components/shared/error-state";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Project } from "@/hooks/use-projects";
import { useTechPartners } from "@/hooks/use-tech-partners";

interface ProjectTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
}

export function ProjectTeamDialog({ open, onOpenChange, project }: ProjectTeamDialogProps) {
  const { data: techPartners = [], isLoading, isError, error, refetch } = useTechPartners();

  const techPartnerMembers = project.product
    ? techPartners
        .filter((tp) => tp.product === project.product)
        .map((tp) => ({ title: "Tech Partner", name: tp.partnerName }))
    : [];

  const members = [
    ...techPartnerMembers,
    { title: "Solution Architect", name: project.solutionArchitect ?? "—" },
    { title: "CSM", name: project.csm ?? "—" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Team</DialogTitle>
          <DialogDescription>{project.name}</DialogDescription>
        </DialogHeader>

        {isError ? (
          <ErrorState message={error?.message ?? "Failed to load team"} onRetry={() => refetch()} />
        ) : (
          <DataTable
            id="project-team"
            headers={[
              { key: "title", label: "Title", defaultWidth: 160 },
              { key: "name", label: "Name", defaultWidth: 200 },
            ]}
            loading={isLoading}
          >
            {members.map((member) => (
              <tr
                key={`${member.title}-${member.name}`}
                className="border-b border-border last:border-0"
              >
                <td className="px-4 py-3 font-medium">{member.title}</td>
                <td className="px-4 py-3 text-muted-foreground">{member.name}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </DialogContent>
    </Dialog>
  );
}
